import { createContext, useContext, useState } from 'react';
import environment from '../env';
import useSWRImmutable from 'swr/immutable';
import useSWR, { SWRConfig } from 'swr/immutable';
import { useClient } from './ClientProvider';
import { useAuth } from './AuthProvider';
import dayjs from 'dayjs';

const LiveLoansContext = createContext();

export const useLiveLoans = () => useContext(LiveLoansContext);

/**
 * Live loans provider
 * @param {*} param0 
 * @returns 
 */
const LiveLoansProvider = ({ children }) => {

  const apiUrl = environment.CUSTOMER_PORTAL_API;

  const { accessToken } = useAuth();

  const { useGetConfig } = useClient();
  const { clientConfig } = useGetConfig();

  const PAYMENT_DATE_CHANGES_ALLOWED_IN_PERIOD = clientConfig?.layout?.arrangements?.paymentDateChangesAllowedInPeriod;
  const PAYMENT_DATE_CHANGES_PERIOD_DAYS = clientConfig?.layout?.arrangements?.paymentDateChangesPeriodDays;

  const defaultOptions = {
    method: "GET",
    headers: {
      "x-api-key": environment.CUSTOMER_PORTAL_API_KEY,
      "Authorization": `Bearer ${accessToken}`,
    },
  };

  /**
   * Get live account SWR hook
   * @returns 
   */
  function useGetLiveAccount() {
    const url = accessToken && `${apiUrl}/live/account`;
    console.info("get live account request: ", { url, defaultOptions });
    const { data, error, isLoading } = useSWRImmutable([url, defaultOptions]);
    return {
      liveAccount: data,
      error,
      isLiveAccountLoading: isLoading,
    };
  };

  function useGetLiveSettlement() {
    const url = accessToken && `${apiUrl}/live/settlement`;
    console.info("get live settlement request: ", { url, defaultOptions });
    const { data, error, isLoading } = useSWRImmutable([url, defaultOptions]);
    return {
      liveSettlement: data,
      error,
      isLiveSettlementLoading: isLoading,
    };
  };

  /**
   * Get payment date changes SWR hook
   * @returns 
   */
  function useGetPaymentDateChanges() {
    const url = accessToken && `${apiUrl}/live/payment-date-change`;
    console.info("get payment date changes request: ", { url, defaultOptions });
    const { data, error, isLoading } = useSWRImmutable([url, defaultOptions]);
    return {
      paymentDateChanges: data,
      error,
      isPaymentDateChangesLoading: isLoading,
    };
  };

  /**
   * Post payment date change
   * @param {*} payload
   * @returns
   */
  async function postPaymentDateChange(payload) {
    let postPaymentDateChangeResponse = null;
    let requestOptions = {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify(payload),
    };
    console.info("post payment date change request: ", { requestOptions });
    await fetch(`${apiUrl}/live/payment-date-change`, requestOptions)
      .then((res) => res.json())
      .then((data) => {
        console.log("post payment date change response: ", data);
        postPaymentDateChangeResponse = data;
      })
      .catch((error) => {
        console.log(`Error in post payment date change: ${error}`);
        return null;
      });
    return postPaymentDateChangeResponse;
  };

  /**
   * Post propose payment date change
   * @param {*} proposal
   * @returns
   */
  async function postProposePaymentDateChange(proposal) {
    let proposalResponse = null;
    let requestOptions = {
      ...defaultOptions,
      method: "POST",
      body: JSON.stringify(proposal),
    };
    console.info("post propose payment date change request: ", { requestOptions });
    await fetch(`${apiUrl}/live/payment-date-change/propose`, requestOptions)
      .then((res) => res.json())
      .then((data) => {
        console.log("post propose payment date change response: ", data);
        proposalResponse = data;
      })
      .catch((error) => {
        console.log(`Error in post propose payment date change: ${error}`);
        return null;
      });
    return proposalResponse;
  };

  function allowPaymentDateChanges(paymentDateChanges) {
    let inPeriodChanges = null
    if (paymentDateChanges.length === 0) {
      inPeriodChanges = []
    }
    else {
      const today = dayjs();
      const currentPeriodStartDate = today.subtract(PAYMENT_DATE_CHANGES_PERIOD_DAYS, "day");
      inPeriodChanges = paymentDateChanges.paymentDateChanges.filter(
        item => dayjs(item.createdWhen).isAfter(currentPeriodStartDate));
    }
    return inPeriodChanges.length >= PAYMENT_DATE_CHANGES_ALLOWED_IN_PERIOD ? false : true;
  }

  const contextValue = {
    useGetLiveAccount,
    useGetPaymentDateChanges,
    postPaymentDateChange,
    postProposePaymentDateChange,
    allowPaymentDateChanges,
    useGetLiveSettlement
  };

  return (
    <LiveLoansContext.Provider value={contextValue}>
      {children}
    </LiveLoansContext.Provider>
  );
};

export default LiveLoansProvider;
